"use client";
import React from "react";
import type { TagFilter } from "@/types/tag-filters";
import { useCatalogParams } from "../hooks/useCatalogParams";
import { useUpdateFilters } from "../hooks/useUpdateFilters";
import { useInventoryNav } from "./InventoryNavContext";
import TagBanner from "./InventoryList.TagBanner";

/**
 * Tag-filter toolbar shown above the grid, mirroring the Location filter:
 * a row of toggle buttons (e.g. "New Items") for `type: "button"` tags, then
 * one full-width banner per `type: "banner"` tag below it. The registry is
 * resolved on the server and passed in; active state is read from the
 * reserved ?tags= URL param.
 *
 * Toggling writes the ?tags= param via the shared filter updater, which keeps
 * tags persistent across other filter changes — so a tag keeps applying in the
 * background while the user works the attribute/Location filters normally.
 */
const TagFilters: React.FC<{ tagFilters: TagFilter[] }> = ({ tagFilters }) => {
  const { tags } = useCatalogParams();
  const { updateFilter } = useUpdateFilters();
  const { runNavigation } = useInventoryNav();

  if (!tagFilters || tagFilters.length === 0) {
    return null;
  }

  const active = new Set(tags);

  const toggle = (key: string) => {
    const next = active.has(key) ? tags.filter((k) => k !== key) : [...tags, key];
    runNavigation(() => updateFilter("tags", next.length > 0 ? next : null));
  };

  const sorted = [...tagFilters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  // `type` is normalized server-side (absent ⇒ "button"), but default here too so
  // a TagFilter built anywhere else still renders sensibly.
  const buttons = sorted.filter((t) => (t.type ?? "button") === "button");
  const banners = sorted.filter((t) => t.type === "banner");

  return (
    <>
      {buttons.length > 0 && (
        <div className="bg-white border-b border-gray-200">
          <div className="flex items-center gap-2 flex-wrap px-3 py-1.5">
            {buttons.map((tag) => {
              const isSelected = active.has(tag.key);
              return (
                <button
                  key={tag.key}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => toggle(tag.key)}
                  className={`hover:cursor-pointer shrink-0 px-4 py-2 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap ${isSelected
                    ? 'bg-gcopper text-white border-gcopper'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                    }`}
                >
                  {/* Label is admin-authored HTML from Cosmos so tags can be styled
                      (bold, color, emoji, etc.) to stand out. Trusted content only. */}
                  <span dangerouslySetInnerHTML={{ __html: tag.label }} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {banners.length > 0 && (
        // 1rem of breathing room between the toolbar rows above and the banner.
        <div className="bg-white pt-4">
          {banners.map((tag) => (
            <TagBanner
              key={tag.key}
              tag={tag}
              isActive={active.has(tag.key)}
              onToggle={() => toggle(tag.key)}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default TagFilters;
